import { Spin } from "antd";
import _ from "lodash";
import React, { useMemo } from "react";
import { useProject } from "../../App/state";
import { useNotification } from "../../contexts/Notifications";
import {
  useGetSceneCoordinatesQuery,
  useGetShotBySceneAndBatchQuery,
  useZeroPointSceneWithBimMutation,
  useZeroPointShotMutation,
} from "../../generated/types";
import { useDualModeState } from "../../pages/DualModePage/state";
import { Batch, PanoView } from "../../types";
import { normaliseYaw } from "../../utils/pano";
import { DualViewer } from "./DualViewer";
import styles from "./DualViewer.module.css";
import { convertShotFragmentToShot } from "./utils";

type DualViewerContainerProps = {
  sceneId?: string;
  referenceBatch: Batch;
  comparisonBatch?: Batch;
  view: PanoView;
  onChangeView: (view: PanoView, viewerId?: string) => void;
  focusedSpotlightId?: string;
  arrowSceneNavigation: (evt: KeyboardEvent) => void;
};

export const DualViewerContainer = (props: DualViewerContainerProps) => {
  const {
    sceneId,
    referenceBatch,
    comparisonBatch,
    view,
    onChangeView,
    focusedSpotlightId,
    arrowSceneNavigation,
  } = props;
  const activeProject = useProject();
  const { currentSceneId } = useDualModeState();
  const notify = useNotification();

  const { data: sceneCoordinatesData } = useGetSceneCoordinatesQuery({
    variables: {
      ...activeProject,
      sceneId: currentSceneId?.value ?? "",
    },
    skip: !currentSceneId?.value,
  });

  const {
    data: lsData,
    loading: lsLoading,
    refetch: lsRefetch,
    error: lsError,
  } = useGetShotBySceneAndBatchQuery({
    variables: {
      sceneId,
      batchId: comparisonBatch?.id as string,
      tenant: activeProject,
    },
    skip: !comparisonBatch,
  });

  const {
    data: rsData,
    loading: rsLoading,
    refetch: rsRefetch,
    error: rsError,
  } = useGetShotBySceneAndBatchQuery({
    variables: {
      sceneId,
      batchId: referenceBatch.id,
      tenant: activeProject,
    },
  });

  const [zeroPointShot] = useZeroPointShotMutation({
    onCompleted: () => {
      notify("Updated zeropoint of reference shot", "success");
    },
    onError: (e) => notify(`Failed to zero point shot: ${e}`, "error"),
  });
  const [zeroPointSceneWithBim] = useZeroPointSceneWithBimMutation({
    onCompleted: () => {
      notify("Updated Bim Difference of Current Scene", "success");
    },
    onError: (e) =>
      notify(`Failed to update Bim Difference of Current Scene: ${e}`, "error"),
  });
  const leftShot = useMemo(
    () =>
      convertShotFragmentToShot(
        lsData?.shot ?? undefined,
        comparisonBatch as Batch,
        "Comparison Shot",
      ),
    [lsData, comparisonBatch],
  );
  const rightShot = useMemo(
    () =>
      convertShotFragmentToShot(
        rsData?.shot ?? undefined,
        referenceBatch,
        "Reference Shot",
      ),
    [rsData, referenceBatch],
  );

  const sceneCoordinates = sceneCoordinatesData?.scene
    ? _.pick(sceneCoordinatesData?.scene, [
        "id",
        "mmX",
        "mmY",
        "mmZOffset",
        "bimDiff",
      ])
    : undefined;
  const resetYaw = (yaw = 0) => {
    onChangeView({
      hfov: view.hfov,
      position: {
        yaw: normaliseYaw(yaw),
        pitch: 0,
      },
    });
  };
  return (
    <div className={styles["dual-viewer"]}>
      <Spin
        wrapperClassName={styles["loading-container"]}
        spinning={lsLoading || rsLoading}
      >
        {lsError || rsError ? (
          <DualViewerError left={lsError?.message} right={rsError?.message} />
        ) : (
          <DualViewer
            view={view}
            resetYaw={resetYaw}
            onChangeView={onChangeView}
            comparisonShot={leftShot}
            referenceShot={rightShot}
            onSpotlightUpdate={() => {
              rsRefetch();
              lsRefetch();
            }}
            focusedSpotlightId={focusedSpotlightId}
            arrowSceneNavigation={arrowSceneNavigation}
            onZeroPoint={(diff) => {
              if (rightShot) {
                zeroPointShot({
                  variables: {
                    customer: activeProject.customer,
                    project: activeProject.project,
                    scope: activeProject.scope,
                    shotId: rightShot.shotId,
                    batchId: referenceBatch.id,
                    yaw: rightShot.imageConfig.yawOffset + diff.yaw,
                  },
                });
              }
            }}
            sceneCoordinates={sceneCoordinates}
            onZeroPointWithBim={(diff) => {
              if (rightShot) {
                zeroPointSceneWithBim({
                  variables: {
                    customer: activeProject.customer,
                    project: activeProject.project,
                    scope: activeProject.scope,
                    yaw: normaliseYaw(diff.yaw),
                    sceneId: currentSceneId?.value ?? "",
                  },
                });
                resetYaw?.(diff.yaw);
              }
            }}
          />
        )}
      </Spin>
    </div>
  );
};

const DualViewerError = ({
  left,
  right,
}: {
  left?: string;
  right?: string;
}) => (
  <div>
    Something went wrong loading scenes: <br />
    {left ? `left scene: ${left}` : ""}
    <br />
    {right ? `right scene: ${right}` : ""}
  </div>
);
